import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {Formik} from 'formik';
import {checkEmailIsValid} from '../../../_common/utils/checkEmailIsValid';
import {integerValueCanStartFromNull} from '../../../_common/utils/changeInputValue';
import FieldInput, {StyledFieldInput} from '../../../_common/components/FieldInput';
import FieldSelect from '../../../_common/components/FieldSelect';
import MultipleTooltip from '../../../_common/components/MultipleTooltip';
import ModalContainer from '../../../_common/components/ModalContainer';
import ButtonAction from '../../../_common/components/ButtonAction';
import ModalContent from '../../../_common/components/ModalContent';
import ModalHeader from '../../../_common/components/ModalHeader';
import ModalButtonsContainer from '../../../_common/components/ModalButtonsContainer';
import AnimatedUploadingFilePopUp from '../../../_common/components/AnimatedUploadingFilePopUp';
import * as actions from '../../actions/CustomersEntityTableActions';
import * as giActions from '../../../_common/actions/greenInvoice';
import {showGlobalMessage} from '../../../_common/components/GlobalMessage/actions/showGlobalMessage';
import {can} from '../../../_common/components/Permissions';
import {buttonCancel, buttonSubmit, largeButtonsPadding} from '../../../_common/styles/styles';
import {useCustomersEntityUIContext} from '../CustomersEntityUIContext';
import {permissionTypes as T, subjects as S} from '../../../_common/constants/permissionSubjects';
import {filterOptions} from '../../../_common/utils/options';
import {customerStatus, customerTypes, genders} from '../../../_common/constants/options';
import {messages as M} from '../../../../../_metronic/i18n/messages';
import styles from './styles.module.scss';

const prefix = 'customers-entity-popup-edit-customer';
const commonProps = {customContainerStyles: 'py-3'};

const FormCustomerTypePerson = ({values}) => {
  const {formatMessage} = useIntl();

  return (
    <>
      <FieldInput
        fieldId={'firstName'}
        label={(
          <div>
            {formatMessage({id: `${prefix}-first-name`, defaultMessage: 'First name'})}
            <span className={'text-danger'}>*</span>
          </div>
        )}
        icon={'/media/common/User_primary.svg'}
        {...commonProps}
      />
      <FieldInput
        fieldId={'lastName'}
        label={(
          <div>
            {formatMessage({id: `${prefix}-last-name`, defaultMessage: 'Last name'})}
            <span className={'text-danger'}>*</span>
          </div>
        )}
        icon={'/media/common/User_primary.svg'}
        {...commonProps}
      />
      <FieldSelect
        fieldId={'genderID'}
        value={filterOptions(values.genderID)(genders)}
        options={genders}
        label={formatMessage({id: `${prefix}-gender`, defaultMessage: 'Gender'})}
        placeholder={formatMessage({id: 'select', defaultMessage: 'Select'})}
        {...commonProps}
      />
      <FieldInput
        fieldId={'idNumber'}
        label={formatMessage({id: `${prefix}-idNumber`, defaultMessage: 'ID number'})}
        icon={'/media/common/Id-badge_primary.svg'}
        {...commonProps}
      />
    </>
  );
};

const FormCustomerTypesCompany = () => {
  const {formatMessage} = useIntl();

  return (
    <>
      <FieldInput
        fieldId={'firstName'}
        label={(
          <div>
            {formatMessage({id: `${prefix}-name`, defaultMessage: 'Name'})}
            <span className={'text-danger'}>*</span>
          </div>
        )}
        icon={'/media/common/User_primary.svg'}
        {...commonProps}
      />
      <FieldInput
        fieldId={'idNumber'}
        label={formatMessage({id: `${prefix}-companyID`, defaultMessage: 'Company ID'})}
        icon={'/media/common/Id-badge_primary.svg'}
        {...commonProps}
      />
    </>
  );
};

const FormCustomerTypeGroup = () => {
  const {formatMessage} = useIntl();

  return (
    <>
      <FieldInput
        fieldId={'firstName'}
        label={(
          <div>
            {formatMessage({id: `${prefix}-groupName`, defaultMessage: 'Group name'})}
            <span className={'text-danger'}>*</span>
          </div>
        )}
        icon={'/media/common/User_primary.svg'}
        {...commonProps}
      />
      <FieldInput
        fieldId={'lastName'}
        label={(
          <div>
            {formatMessage({id: `${prefix}-groupSubName`, defaultMessage: 'Group sub name'})}
            <span className={'text-danger'}>*</span>
          </div>
        )}
        icon={'/media/common/User_primary.svg'}
        {...commonProps}
      />
      <FieldInput
        fieldId={'idNumber'}
        label={formatMessage({id: `${prefix}-groupID`, defaultMessage: 'Group ID'})}
        icon={'/media/common/Id-badge_primary.svg'}
        {...commonProps}
      />
    </>
  );
};

const CustomerDataForm = ({close, formikProps}) => {
  const {formatMessage} = useIntl();
  const dispatch = useDispatch();
  const [emailError, setEmailError] = useState(false);
  const [creating, setCreating] = useState(false);
  const {queryParams} = useCustomersEntityUIContext();
  const updateTable = async () => {
    await dispatch(actions.getCustomers(queryParams));
  };
  const {
    greenInvoiceApiKey,
    greenInvoiceSecret
  } = useSelector((state) => state.auth, shallowEqual);

  const initialValues = {
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    idNumber: '',
    address: '',
    genderID: null,
    customerType: null,
    status: 1,
    customerID: -1
  };

  const validate = (values) => {
    setEmailError(false);
    let errors = {};

    if (!values.customerType) {
      errors.customerType = 'Required';
    }

    switch (values.customerType) {
      case 1:
      case 2:
      case 3:
        ['firstName'].forEach(function (item) {
          if (!values[item]) errors[item] = 'Required';
        });
        break;
      case 4:
        ['firstName', 'lastName'].forEach(function (item) {
          if (!values[item]) errors[item] = 'Required';
        });
        break;
      case 5:
        ['firstName', 'lastName'].forEach(function (item) {
          if (!values[item]) errors[item] = 'Required';
        });
        break;
    }

    if (values.email && !checkEmailIsValid(values.email)) {
      errors['email'] = 'Invalid email address';
      setEmailError(true);
    }

    return errors;
  };

  const buildAddCustomerBody = (values) => {
    switch (values.customerType) {
      case 1:
      case 2:
      case 3:
        return {...values, lastName: '', genderID: null};
      case 4:
        return {...values};
      case 5:
        return {...values, genderID: null};
    }
  };

  const createClient = async ({
                                customerID,
                                firstName,
                                lastName,
                                phone,
                                email,
                                idNumber,
                                address
                              }) => {
    return await dispatch(giActions.createClient({
      customerID,
      name: [firstName, lastName].filter(Boolean).join(' '),
      phone,
      emails: email ? [email] : [],
      taxId: idNumber,
      address
    }, {greenInvoiceApiKey, greenInvoiceSecret}));
  };

  const addNewToCustomersList = (customerID, data, greenInvoiceClientID) => {
    if (formikProps) {
      const customerData = {
        customerID,
        email: data?.email,
        firstName: data?.firstName,
        idNumber: data?.idNumber,
        lastName: data?.lastName,
        phone: data?.phone,
        greenInvoiceClientID
      };
      formikProps.setFieldValue('customers', [...formikProps.values.customers, customerData]);
    }
  };

  const addCustomerData = (values) => async () => {
    setCreating(true);

    try {
      const data = buildAddCustomerBody(values);
      const customerID = await dispatch(actions.addCustomer(data));

      if (!customerID) {
        dispatch(showGlobalMessage('error', M['-1']));
        return;
      }

      if (greenInvoiceApiKey && greenInvoiceSecret && customerID) {
        const {errorCode, body} = await createClient({...data, customerID});

        addNewToCustomersList(customerID, data, body?.id ?? '');

        if (customerID && errorCode === 0) {
          dispatch(showGlobalMessage('success', formatMessage({id: 'data-sent-success', defaultMessage: 'Success'})));
        } else {
          dispatch(showGlobalMessage('warning',
            [
              formatMessage({id: 'customer-was-created', defaultMessage: 'Customer was created'}),
              M.GREEN_INVOICE_CREATE_CUSTOMER_ERROR, body?.errorMessage ? `: ${body.errorMessage}` : ''
            ]
              .filter(Boolean)
              .join(`. `)
          ));
        }
      } else {
        addNewToCustomersList(customerID, data, '');
      }
    } catch (e) {
      console.error('addCustomerData error:', e);
      dispatch(showGlobalMessage('warning',
        [
          formatMessage({id: 'customer-was-created', defaultMessage: 'Customer was created'}),
          M.GREEN_INVOICE_CREATE_CUSTOMER_ERROR
        ]
          .filter(Boolean)
          .join(`. `)
      ));
    } finally {
      setTimeout(() => {
        if (!formikProps) updateTable();
        close();
        setCreating(false);
      }, 3000);
    }
  };

  const renderFormByCustomerType = (values) => {
    switch (values.customerType) {
      case 1:
      case 2:
      case 3:
        return <FormCustomerTypesCompany/>;
      case 4:
        return <FormCustomerTypePerson values={values}/>;
      case 5:
        return <FormCustomerTypeGroup/>;
      default:
        return null;
    }
  };

  const resetFields = (values, setFieldValue) => {
    switch (values.customerType) {
      case 1:
      case 2:
      case 3:
        setFieldValue('lastName', initialValues.lastName);
        setFieldValue('genderID', initialValues.genderID);
        break;
      case 4:
        break;
      case 5:
        setFieldValue('genderID', initialValues.genderID);
        break;
    }
  };

  return (
    <>
      <AnimatedUploadingFilePopUp show={creating}/>
      <Formik
        initialValues={initialValues}
        onSubmit={() => {
        }}
        enableReinitialize={true}
        validate={validate}
      >
        {({values, setFieldValue, dirty, isValid}) => (
          <>
            <ModalContent customClassnames={'bg-transparent'}>
              <div className={'d-flex justify-content-between'}>
                <div className={'w-50 mr-4'}>
                  <FieldSelect
                    fieldId={'customerType'}
                    value={filterOptions(values.customerType)(customerTypes)}
                    options={customerTypes}
                    label={
                      <div>
                        {formatMessage({id: `${prefix}-type`, defaultMessage: 'Type'})}
                        <span className={'text-danger'}>*</span>
                      </div>
                    }
                    onChange={() => resetFields(values, setFieldValue)}
                    customContainerStyles={'py-3'}
                  />
                  {renderFormByCustomerType(values)}
                </div>
                <div className={'w-50 ml-4'}>
                  <StyledFieldInput
                    fieldId={'phone'}
                    label={
                      <div className={'d-flex align-items-center'}>
                    <span className={'mr-2'}>{formatMessage({
                      id: `${prefix}-phone`,
                      defaultMessage: 'Phone'
                    })}</span>
                        <MultipleTooltip
                          id={'phone'}
                          label={formatMessage({
                            id: `${prefix}-phone-tooltip`,
                            defaultMessage: 'Please enter the phone number in digits only without special characters'
                          })}
                          customIconClassName={'cursor-pointer position-static'}
                        />
                      </div>
                    }
                    value={values.phone}
                    onChange={e => setFieldValue('phone', integerValueCanStartFromNull(e))}
                    icon={'/media/common/Phone_primary.svg'}
                    {...commonProps}
                  />
                  <div className={'position-relative'}>
                    <FieldInput
                      dir={'ltr'}
                      fieldId={'email'}
                      label={formatMessage({id: `${prefix}-email`, defaultMessage: 'Email'})}
                      type={'email'}
                      customInputStyles={'text-left'}
                      icon={'/media/common/Email_primary.svg'}
                      {...commonProps}
                    />
                    {emailError && (
                      <div className={styles.invalidEmailMessage}>
                        {formatMessage({id: 'invalid-email-message', defaultMessage: 'Invalid email'})}
                      </div>
                    )}
                  </div>
                  <FieldInput
                    fieldId={'address'}
                    label={formatMessage({id: `${prefix}-address`, defaultMessage: 'Address'})}
                    icon={'/media/common/Address_primary.svg'}
                    {...commonProps}
                  />
                  <FieldSelect
                    fieldId={'status'}
                    value={filterOptions(values.status)(customerStatus)}
                    options={customerStatus}
                    label={formatMessage({id: `${prefix}-status`, defaultMessage: 'Status'})}
                    customContainerStyles={'py-3'}
                  />
                </div>
              </div>
            </ModalContent>
            <ModalButtonsContainer customClassnames={largeButtonsPadding}>
              <ButtonAction
                variant={'cancel'}
                onClick={close}
                label={formatMessage({id: 'button-close', defaultMessage: 'Close'})}
                customClassnames={buttonCancel}
              />
              <ButtonAction
                onClick={addCustomerData(values)}
                disabled={!dirty || !isValid}
                useIcon={'/media/common/Check-in-circle_white.svg'}
                label={formatMessage({id: 'button-submit', defaultMessage: 'Submit'})}
                customClassnames={buttonSubmit}
              />
            </ModalButtonsContainer>
          </>
        )}
      </Formik>
    </>
  );
};

export default function PopupAddCustomer({
                                           buttonVariant = 'link',
                                           customLabel = '',
                                           formikProps,
                                           containerClassnames = ''
                                         }) {
  const [show, setShow] = useState(false);
  const handleCloseModal = () => setShow(false);
  const handleShowModal = () => setShow(true);
  const {formatMessage} = useIntl();

  return (
    <div className={containerClassnames}>
      <ButtonAction
        variant={buttonVariant}
        onClick={handleShowModal}
        disabled={!can(T.FULL, S.CUSTOMERS_ENTITY_PAGE)}
        label={customLabel ||
          [
            formatMessage({id: 'customers-entity-add-row-button', defaultMessage: 'Add customer'}),
            '+'
          ].filter(Boolean).join(' ')
        }
      />
      <ModalContainer show={show} onHide={handleCloseModal} customClassnames={'pt-5'}>
        <ModalHeader header={formatMessage({id: `${prefix}-header`, defaultMessage: 'Add customer'})}/>
        <CustomerDataForm close={handleCloseModal} formikProps={formikProps}/>
      </ModalContainer>
    </div>
  );
}